import type { Schemas } from "@softmax/observatory-client/types";
import type { EpisodeRow, PlayEpisodes } from "../types";
import { episodeRow } from "./episode-row";
import { median } from "./episode-scores";
import type { EpisodesViewer } from "./episode-seats";

export function buildEpisodes({
  entries,
  viewer,
  nextCursor,
  episodeHrefOf,
  replayHrefOf,
}: {
  readonly entries: readonly Schemas["EpisodeSummary"][];
  readonly viewer: EpisodesViewer;
  readonly nextCursor: string | null;
  readonly episodeHrefOf: (episodeId: string) => string;
  readonly replayHrefOf: (episodeId: string) => string;
}): PlayEpisodes {
  const rows: EpisodeRow[] = [];

  for (const entry of entries) {
    const row = episodeRow({ entry, viewer, episodeHrefOf, replayHrefOf });

    if (row) rows.push(row);
  }

  // The median prevents one extreme score from skewing every comparison.
  const medianScore =
    rows.length > 0 ? median(rows.map((row) => row.score)) : null;
  const finishedRows =
    medianScore === null
      ? rows
      : rows.map((row) => ({
          ...row,
          scoreDeltaVsMedian: row.score - medianScore,
        }));
  return {
    rows: finishedRows,
    summary: {
      loaded: finishedRows.length,
      won: finishedRows.filter((row) => row.won === true).length,
      lost: finishedRows.filter((row) => row.won === false).length,
      hasWinSignal: finishedRows.some((row) => row.won !== null),
      medianScore,
    },
    nextCursor,
  };
}
